import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { formatEatDateTime } from '@wodi/domain';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  EmptyState,
  LoadingBlock,
  Notice,
  PageHeader,
  Tag,
  TextField,
} from '@wodi/ui';
import { useState } from 'react';

import { ApiError } from '../api/client.ts';
import {
  pairingLink,
  pairQueueDisplay,
  queueDisplaysQuery,
  revokeQueueDisplay,
} from '../api/displays.ts';
import { can, sessionQuery } from '../api/session.ts';
import { queuesQuery } from '../api/triage.ts';
import { useOnline } from '../hooks/use-online.ts';
import { toasts } from '../toasts.ts';
import styles from '../triage/clinical-rules.module.css';
import { SettingsPage } from './settings-nav.tsx';

// Settings > Queue displays (triage plan §6.4; ADR 0013). A display is paired once with a
// link that is shown once; it then shows ticket numbers only. Revoking disconnects it.

function message(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

function PairDisplay({ onDone }: { onDone: () => void }) {
  const queryClient = useQueryClient();
  const points = useQuery(queuesQuery);
  const [name, setName] = useState('Waiting area');
  const [chosen, setChosen] = useState<string[]>([]);
  const [link, setLink] = useState<string | null>(null);
  const pair = useMutation({
    mutationFn: () => pairQueueDisplay({ name: name.trim(), servicePointIds: chosen }),
    onSuccess: async (paired) => {
      await queryClient.invalidateQueries({ queryKey: queueDisplaysQuery.queryKey });
      setLink(pairingLink(window.location.origin, paired.pairingToken));
    },
  });
  if (link !== null) {
    return (
      <div className={styles.editor}>
        <p>
          Open this link once on the display's browser. It is shown only now; if you lose it, revoke
          the display and pair it again.
        </p>
        <TextField label="Pairing link" value={link} isReadOnly />
        <div>
          <Button variant="primary" onPress={onDone}>
            Done
          </Button>
        </div>
      </div>
    );
  }
  return (
    <div className={styles.editor}>
      <TextField label="Name" value={name} onChange={setName} />
      <fieldset className={styles.ruleRow}>
        <legend className={styles.legend}>Queues to show</legend>
        {points.isPending ? <LoadingBlock label="Loading queues" rows={2} /> : null}
        {(points.data ?? []).map((p) => (
          <Checkbox
            key={p.id}
            isSelected={chosen.includes(p.id)}
            onChange={(on) => {
              setChosen((c) => (on ? [...c, p.id] : c.filter((id) => id !== p.id)));
            }}
          >
            {p.name}
          </Checkbox>
        ))}
      </fieldset>
      <p className={styles.help}>
        The display shows ticket numbers and these queues' names only. Avoid queue names that reveal
        why someone is waiting.
      </p>
      <div className={styles.actions}>
        <Button
          variant="primary"
          isDisabled={name.trim() === '' || chosen.length === 0}
          isPending={pair.isPending}
          pendingLabel="Pairing…"
          onPress={() => {
            pair.mutate();
          }}
        >
          Pair display
        </Button>
        <Button variant="quiet" onPress={onDone}>
          Cancel
        </Button>
      </div>
      {pair.error === null ? null : (
        <Notice tone="error">{message(pair.error, 'The display could not be paired.')}</Notice>
      )}
    </div>
  );
}

export function QueueDisplaysPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const queryClient = useQueryClient();
  const online = useOnline();
  const displays = useQuery(queueDisplaysQuery);
  const points = useQuery(queuesQuery);
  const [pairing, setPairing] = useState(false);
  const [revoking, setRevoking] = useState<string | null>(null);
  const revoke = useMutation({
    mutationFn: (id: string) => revokeQueueDisplay(id),
    onSuccess: async () => {
      setRevoking(null);
      await queryClient.invalidateQueries({ queryKey: queueDisplaysQuery.queryKey });
      toasts.show({ title: 'Display disconnected' });
    },
  });

  if (session === null || !can(session, 'admin.facilities.manage')) {
    return (
      <>
        <PageHeader title="Queue displays" />
        <Notice tone="info">
          You don't have permission to manage displays. Ask an administrator if you need it.
        </Notice>
      </>
    );
  }
  const nameOf = new Map((points.data ?? []).map((p) => [p.id, p.name]));
  return (
    <SettingsPage
      title="Queue displays"
      panel
      actions={
        pairing ? undefined : (
          <Button
            isDisabled={!online}
            onPress={() => {
              setPairing(true);
            }}
          >
            Pair a display
          </Button>
        )
      }
    >
      <div className={styles.page}>
        {pairing ? (
          <PairDisplay
            onDone={() => {
              setPairing(false);
            }}
          />
        ) : null}
        {displays.isPending ? <LoadingBlock label="Loading displays" rows={3} /> : null}
        {displays.isError ? (
          <Notice
            tone="error"
            action={
              <Button dense onPress={() => void displays.refetch()}>
                Try again
              </Button>
            }
          >
            {message(displays.error, 'The displays could not be loaded. Try again.')}
          </Notice>
        ) : null}
        {displays.isSuccess && displays.data.length === 0 && !pairing ? (
          <EmptyState>
            No displays yet. Pair a waiting-area screen to show ticket numbers.
          </EmptyState>
        ) : null}
        {displays.isSuccess && displays.data.length > 0 ? (
          <ul className={styles.rules}>
            {displays.data.map((d) => (
              <li key={d.id} className={styles.rule}>
                <span className={styles.ruleText}>
                  {`${d.name}: ${d.servicePointIds.map((id) => nameOf.get(id) ?? 'A removed queue').join(', ')}`}
                </span>
                <span className={styles.ruleSource}>
                  {d.lastSeenAt === null
                    ? 'Not seen yet'
                    : `Last seen ${formatEatDateTime(new Date(d.lastSeenAt))}`}
                </span>
                <span className={styles.actions}>
                  {d.status === 'active' ? (
                    <>
                      <Tag state="active">Connected</Tag>
                      <Button
                        dense
                        variant="quiet"
                        isDisabled={!online}
                        onPress={() => {
                          setRevoking(d.id);
                        }}
                      >
                        Disconnect
                      </Button>
                    </>
                  ) : (
                    <Tag state="neutral">Disconnected</Tag>
                  )}
                </span>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
      <Dialog
        title="Disconnect the display"
        isOpen={revoking !== null}
        onOpenChange={(open) => {
          if (!open) setRevoking(null);
        }}
      >
        <p>The screen stops showing tickets at its next refresh. Pair it again to reconnect.</p>
        {revoke.error === null ? null : (
          <Notice tone="error">{message(revoke.error, 'It could not be disconnected.')}</Notice>
        )}
        <DialogActions>
          <Button
            variant="quiet"
            onPress={() => {
              setRevoking(null);
            }}
          >
            Keep it
          </Button>
          <Button
            variant="primary"
            isPending={revoke.isPending}
            pendingLabel="Disconnecting…"
            onPress={() => {
              if (revoking !== null) revoke.mutate(revoking);
            }}
          >
            Disconnect
          </Button>
        </DialogActions>
      </Dialog>
    </SettingsPage>
  );
}
